import {
  type Dispatch,
  type SetStateAction,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { useDebouncedValue } from './useDebouncedValue';

export interface ListFilterConfig<F> {
  empty: F;
  countActive: (filters: F) => number;
}

export interface UseListFilterOptions<F, Q = Record<string, string>> {
  filter?: ListFilterConfig<F>;
  searchTerm?: string;
  toQueryParams?: (searchTerm: string, filters: F) => Q;
}

export type UseListFilterReturn<F, Q = Record<string, string>> = {
  filters: F;
  setFilters: Dispatch<SetStateAction<F>>;
  clearFilters: () => void;
  filterOpen: boolean;
  openFilters: () => void;
  closeFilters: () => void;
  activeFilterCount: number;
  getQuery: () => Q | undefined;
};

export function useListFilter<F, Q = Record<string, string>>(
  options: UseListFilterOptions<F, Q>
): UseListFilterReturn<F, Q> {
  const { filter, searchTerm = '', toQueryParams } = options;

  const enabled = filter != null;
  const empty = filter?.empty ?? (undefined as F);
  const isServerSide = toQueryParams != null;

  const [filters, setFilters] = useState(empty);
  const [filterOpen, setFilterOpen] = useState(false);
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300, isServerSide);

  const activeFilterCount = useMemo(() => {
    if (!enabled || !filter) {
      return 0;
    }
    return filter.countActive(filters);
  }, [enabled, filter, filters]);

  const clearFilters = useCallback(() => {
    if (enabled) {
      setFilters(empty);
    }
  }, [enabled, empty]);

  const openFilters = useCallback(() => {
    if (enabled) {
      setFilterOpen(true);
    }
  }, [enabled]);

  const closeFilters = useCallback(() => {
    setFilterOpen(false);
  }, []);

  const query = useMemo(() => {
    if (!isServerSide || !toQueryParams) {
      return undefined;
    }
    return toQueryParams(debouncedSearchTerm, filters);
  }, [isServerSide, toQueryParams, debouncedSearchTerm, filters]);

  const getQuery = useCallback(() => query, [query]);

  return {
    filters,
    setFilters,
    clearFilters,
    filterOpen,
    openFilters,
    closeFilters,
    activeFilterCount,
    getQuery,
  };
}

export function applyListFilter<T, F>(
  items: T[],
  filters: F,
  match?: (item: T, filters: F) => boolean
): T[] {
  if (!match) {
    return items;
  }

  return items.filter((item) => match(item, filters));
}
